/**
 * Parallax Module
 * Adds subtle parallax scrolling effect to background images
 */

import { initResponsiveBackgrounds } from './responsive-images.js';

/**
 * Initialize parallax effects
 */
export const initParallax = () => {
    // Skip parallax if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    // Background parallax sections (stats section, banner slides)
    const parallaxSections = document.querySelectorAll('[data-parallax]');

    if (parallaxSections.length === 0) return;

    // Set background images - support both old and new formats
    parallaxSections.forEach((section) => {
        // Check if this element has responsive background data
        const hasResponsiveBg = section.hasAttribute('data-bg-mobile') ||
                                section.hasAttribute('data-bg-tablet') ||
                                section.hasAttribute('data-bg-desktop');

        if (hasResponsiveBg) {
            // Use responsive backgrounds (handled by initResponsiveBackgrounds)
            // This is handled in initResponsiveImages, but we need to wait for it
            // So we'll call it here too to ensure it's initialized
            initResponsiveBackgrounds([section]);
        } else {
            // Fallback to old data-bg-image attribute
            const bgImage = section.getAttribute('data-bg-image');
            if (bgImage) {
                console.log('Setting background image:', bgImage);
                section.style.backgroundImage = `url('${bgImage}')`;
            }
        }
    });

    // Use requestAnimationFrame for smooth performance
    let ticking = false;

    const updateParallax = () => {
        const windowHeight = window.innerHeight;

        // Update background parallax sections
        parallaxSections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            const sectionTop = rect.top;
            const sectionHeight = rect.height;

            // Apply parallax even before section enters viewport for better coverage
            const speed = parseFloat(section.getAttribute('data-parallax-speed')) || 0.5;

            // Calculate the percentage of the section visible
            const scrolled = windowHeight - sectionTop;
            const percentage = scrolled / (windowHeight + sectionHeight);

            // Adjusted parallax calculation for better coverage
            const yPos = 30 - (percentage * 60 * speed);
            section.style.backgroundPosition = `center ${yPos}%`;
        });

        ticking = false;
    };

    const requestTick = () => {
        if (!ticking) {
            requestAnimationFrame(updateParallax);
            ticking = true;
        }
    };

    // Listen to scroll events
    window.addEventListener('scroll', requestTick, { passive: true });

    // Initial call to set positions
    updateParallax();

    // Update on window resize
    window.addEventListener('resize', () => {
        if (!ticking) {
            requestAnimationFrame(updateParallax);
        }
    }, { passive: true });
};
